import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useDirection } from '../../../contexts/DirectionContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { Overlay } from '../Overlay';
import { Icon } from '@iconify/react';

export interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  header?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  position?: 'left' | 'right';
  width?: string;
  className?: string;
}

export const Drawer: React.FC<DrawerProps> = ({
  isOpen,
  onClose,
  header,
  children,
  footer,
  position: positionProp,
  width = '280px',
  className = '',
}) => {
  const { isRTL } = useDirection();
  const { theme } = useTheme();

  // Determine the position based on RTL and prop
  const position = positionProp || (isRTL ? 'right' : 'left');

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] isolate">
      <Overlay isVisible={isOpen} onClick={onClose} className="bg-surface-primary/50 -z-[1]" />
      <div
        className={`
          absolute inset-y-0
          ${position === 'right' ? 'right-0' : 'left-0'}
          flex flex-col h-full
          bg-surface-primary
          border-${position === 'right' ? 'l' : 'r'} border-border-primary
          shadow-lg
          transition-transform duration-300
          ${className}
        `}
        style={{ width }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        {header ? (
          <div className="flex-none flex items-center justify-between p-4 border-b border-border-primary bg-surface-secondary">
            {header}
            <button
              onClick={onClose}
              className="p-2 hover:bg-surface-tertiary rounded-lg text-text-primary"
              aria-label="Close drawer"
            >
              <Icon icon="ph:x-thin" className="w-5 h-5" />
            </button>
          </div>
        ) : (
          <div className="flex-none flex justify-end p-4 border-b border-border-primary bg-surface-secondary">
            <button
              onClick={onClose}
              className="p-2 hover:bg-surface-tertiary rounded-lg text-text-primary"
              aria-label="Close drawer"
            >
              <Icon icon="ph:x-thin" className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="p-4">
            {children}
          </div>
        </div>

        {/* Footer */}
        {footer && (
          <div className="flex-none p-4 border-t border-border-primary bg-surface-secondary">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}; 